import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { 
    FaFilePdf,
    FaArrowLeft,
    FaSort,
    FaPrint,
    FaFileExcel,
    FaCopy,
    FaSearch
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const EngineerSocietyForm65 = () => {
    const navigate = useNavigate();
    const [allWorks, setAllWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTaluka, setSelectedTaluka] = useState('');
    const [selectedYear, setSelectedYear] = useState('');

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    const talukaList = [
        { en: 'Miraj', mr: 'मिरज' },
        { en: 'Kavathe Mahankal', mr: 'कवठे महांकाळ' },
        { en: 'Tasgaon', mr: 'तासगाव' },
        { en: 'Jath', mr: 'जत' },
        { en: 'Khanapur', mr: 'खानापूर (विटा)' },
        { en: 'Atpadi', mr: 'आटपाडी' },
        { en: 'Palus', mr: 'पलूस' },
        { en: 'Kadegaon', mr: 'कडेगाव' },
        { en: 'Shirala', mr: 'शिराळा' },
        { en: 'Walwa', mr: 'वाळवा (इस्लामपूर)' }
    ];

    const getMarathiTaluka = (en) => {
        const t = talukaList.find(item => item.en.toLowerCase() === (en || "").toLowerCase());
        return t ? t.mr : en;
    };

    useEffect(() => {
        setLoading(true);
        const workRef = ref(db, 'ekamwatapzp/generatework');
        
        onValue(workRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattened = [];
                
                Object.entries(data).forEach(([dateKey, works]) => {
                    if (works && typeof works === 'object') {
                        if (works.workName || works.schemeName) {
                            flattened.push({
                                id: dateKey,
                                lotteryDate: dateKey,
                                ...works,
                                localStatus: works.status || "स्थिती",
                                localRemark: works.remarks || ""
                            });
                        } else {
                            Object.entries(works).forEach(([workId, workData]) => {
                                if (workData && typeof workData === 'object') {
                                    flattened.push({
                                        id: workId,
                                        lotteryDate: dateKey,
                                        ...workData,
                                        localStatus: workData.status || "स्थिती",
                                        localRemark: workData.remarks || ""
                                    });
                                }
                            });
                        }
                    }
                });

                const filtered = flattened.filter(work => 
                    work.category === "Society" && 
                    (work.societyName || work.workName)
                );
                
                setAllWorks(filtered.reverse());
            } else {
                setAllWorks([]);
            }
            setLoading(false);
        });
    }, []);

    const filteredData = useMemo(() => {
        return allWorks.filter(item => {
            const matchesTaluka = !selectedTaluka || (item.taluka && item.taluka.toLowerCase() === selectedTaluka.toLowerCase());
            const matchesYear = !selectedYear || (item.financialYear && item.financialYear.includes(selectedYear));
            const matchesSearch = !searchTerm || 
                (item.societyName && item.societyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (item.workName && item.workName.toLowerCase().includes(searchTerm.toLowerCase()));
            
            return matchesTaluka && matchesYear && matchesSearch;
        });
    }, [allWorks, selectedTaluka, selectedYear, searchTerm]);

    const handleUpdate = async (id, updatedStatus, updatedRemark) => {
        const path = `ekamwatapzp/generatework/${id}`;
        try {
            await update(ref(db, path), {
                status: updatedStatus,
                remarks: updatedRemark
            });
            toast.success("माहिती यशस्वीरित्या अपडेट झाली!");
        } catch (error) {
            console.error("Update error:", error);
            toast.error("अपडेट करताना त्रुटी आली.");
        }
    };

    const downloadExcel = () => {
        const excelData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'संस्थेचे नाव': item.societyName || "-",
            'कामाचे नाव': item.workName || "-",
            'लॉटरी दिनांक': item.lotteryDate || "-",
            'स्थिती': item.status || "-",
            'शेरा': item.remarks || "-"
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Society_Form_65");
        XLSX.writeFile(wb, `Society_Form_65_${new Date().toLocaleDateString()}.xlsx`);
    };

    const copyToClipboard = () => {
        const headers = ['अ.क्र.', 'संस्थेचे नाव', 'कामाचे नाव', 'लॉटरी दिनांक', 'स्थिती', 'शेरा'];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item.societyName || "-",
            item.workName || "-",
            item.lotteryDate || "-",
            item.status || "-",
            item.remarks || "-"
        ]);

        const content = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("माहिती कॉपी झाली!");
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe] font-marathi">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-8 px-2 no-print">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/engineer/form-65-menu')}
                            className="p-2.5 bg-white rounded-xl shadow-sm hover:translate-x-[-2px] transition text-gray-400 border border-gray-100 hover:text-blue-500"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800 tracking-tight"> संस्था फॉर्म ६५ (Society Form 65)</h1>
                    </div>
                    <div className="text-[11px] font-black text-gray-400 uppercase tracking-widest bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-50">
                        सांगली जिल्हा परिषद / <span className="text-gray-600">पहा संस्था फॉर्म ६५</span>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    {/* Filters Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 no-print">
                        <div className="space-y-2">
                            <label className="text-xs font-black text-gray-400 uppercase tracking-wider ml-1">तालुका निवडा</label>
                            <select 
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold bg-gray-50/50 text-gray-600"
                                value={selectedTaluka}
                                onChange={(e) => setSelectedTaluka(e.target.value)}
                            >
                                <option value="">सर्व तालुके</option>
                                {talukaList.map(t => (
                                    <option key={t.en} value={t.en}>{t.mr}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black text-gray-400 uppercase tracking-wider ml-1">आर्थिक वर्ष निवडा</label>
                            <select 
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold bg-gray-50/50 text-gray-600"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                <option value="">सर्व वर्षे</option>
                                <option value="2024">२०२४</option>
                                <option value="2025">२०२५</option>
                                <option value="2026">२०२६</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button 
                                className="w-full bg-[#4e73df] hover:bg-blue-700 text-white h-[50px] rounded-xl font-black text-sm shadow-lg shadow-blue-500/20 transition active:scale-95 flex items-center justify-center gap-2"
                            >
                                <FaSearch /> सर्च करा
                            </button>
                        </div>
                    </div>

                    <div className="border-t border-gray-50 pt-8 mt-2">
                        {/* Toolbar */}
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8 no-print">
                            <div className="flex bg-[#6c757d] rounded overflow-hidden shadow-sm">
                                <button onClick={copyToClipboard} className="hover:bg-gray-700 text-white px-5 py-2 text-xs font-bold transition border-r border-gray-500/50 flex items-center gap-2">
                                    <FaCopy size={12} /> कॉपी करा
                                </button>
                                <button onClick={downloadExcel} className="hover:bg-gray-700 text-white px-5 py-2 text-xs font-bold border-r border-gray-500/50 transition flex items-center gap-2">
                                    <FaFileExcel size={12} /> एक्सेल
                                </button>
                                <button onClick={() => window.print()} className="hover:bg-gray-700 text-white px-5 py-2 text-xs font-bold transition flex items-center gap-2">
                                    <FaPrint size={12} /> प्रिंट
                                </button>
                            </div>
                            <div className="flex items-center gap-3 w-full md:w-auto">
                                <label className="text-sm font-bold text-gray-500 uppercase tracking-wider">शोधा:</label>
                                <input 
                                    type="text" 
                                    className="border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold bg-gray-50/50 w-full md:w-80"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="संस्थेचे नाव किंवा काम..."
                                />
                            </div>
                        </div>

                        {/* Table Section */}
                        <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-[#f8f9fc] border-b border-gray-100">
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-center uppercase tracking-wider w-20 border-r border-gray-50">अ.क्र.</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 min-w-[220px]">संस्थेचे नाव</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 min-w-[280px]">कामाचे नाव</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-center uppercase tracking-wider border-r border-gray-50 w-32">लॉटरी दिनांक</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-center border-r border-gray-50 w-28">फॉर्म ६५</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 w-44">स्थिती</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 min-w-[200px]">शेरा</th>
                                        <th className="px-6 py-4 text-[12px] font-black text-gray-500 text-center uppercase tracking-wider w-32 bg-gray-50/10">कृती</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredData.length > 0 ? filteredData.map((item, index) => (
                                        <tr key={item.id} className="hover:bg-blue-50/20 group transition-colors text-[13px] text-gray-600 font-bold">
                                            <td className="px-6 py-6 text-center border-r border-gray-50 text-gray-400">{toMarathiNumber(index + 1)}</td>
                                            <td className="px-6 py-6 border-r border-gray-50">
                                                <div className="space-y-1">
                                                    <div className="text-gray-900 font-marathi font-black leading-tight">{item.societyName}</div>
                                                    <div className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">{getMarathiTaluka(item.taluka)}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 border-r border-gray-50 leading-relaxed font-marathi">
                                                <div className="text-gray-800 line-clamp-2">{item.workName}</div>
                                                <div className="text-[11px] text-blue-500 font-black mt-2 uppercase tracking-widest">{toMarathiNumber(item.tenderAmount || item.amount || "०")}</div>
                                            </td>
                                            <td className="px-6 py-6 border-r border-gray-50 text-center font-marathi text-gray-500 italic">{toMarathiNumber(item.lotteryDate || "-")}</td>
                                            <td className="px-6 py-6 border-r border-gray-50 text-center">
                                                <div className="inline-flex items-center justify-center p-2.5 bg-white border border-red-100 rounded-xl cursor-pointer hover:bg-red-50 transition-all group/pdf shadow-sm">
                                                    <FaFilePdf size={18} className="text-red-500 group-hover/pdf:scale-110 transition-transform" />
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 border-r border-gray-50">
                                                <select 
                                                    className="w-full border border-gray-100 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-100 bg-white text-gray-500 font-bold text-[11px] appearance-none"
                                                    value={item.localStatus}
                                                    onChange={(e) => {
                                                        const updated = [...allWorks];
                                                        const idx = allWorks.findIndex(w => w.id === item.id);
                                                        updated[idx].localStatus = e.target.value;
                                                        setAllWorks(updated);
                                                    }}
                                                >
                                                    <option value="स्थिती">स्थिती निवडा</option>
                                                    <option value="In Progress">काम सुरू आहे</option>
                                                    <option value="Completed">काम पूर्ण झाले</option>
                                                </select>
                                            </td>
                                            <td className="px-6 py-6 border-r border-gray-50">
                                                <input 
                                                    type="text" 
                                                    className="w-full border border-gray-50 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-blue-100 bg-gray-50/50 text-[11px] font-bold placeholder:text-gray-300 transition-all"
                                                    placeholder="शेरा प्रविष्ट करा"
                                                    value={item.localRemark}
                                                    onChange={(e) => {
                                                        const updated = [...allWorks];
                                                        const idx = allWorks.findIndex(w => w.id === item.id);
                                                        updated[idx].localRemark = e.target.value;
                                                        setAllWorks(updated);
                                                    }}
                                                />
                                            </td>
                                            <td className="px-6 py-6 text-center bg-gray-50/10">
                                                <button 
                                                    onClick={() => handleUpdate(item.id, item.localStatus, item.localRemark)}
                                                    className="bg-[#4e73df] hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 transition transform active:scale-95 w-full whitespace-nowrap"
                                                >
                                                    अपडेट करा
                                                </button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-20 text-center text-gray-400 font-bold italic bg-gray-50/30 tracking-widest uppercase">
                                                कोणतीही माहिती उपलब्ध नाही.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .shadow-sm { box-shadow: none !important; }
                    .rounded-2xl { border-radius: 0 !important; }
                    .p-8 { padding: 0 !important; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSocietyForm65;
